import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export interface SlaJobTypeStat {
  jobTypeId: string;
  jobTypeName: string;
  metCount: number;
  missedCount: number;
  total: number;
  complianceRate: number;
}

export type SlaComplianceTier = "hot" | "cold";
export type SlaComplianceBucket = "day" | "week" | "month";

export interface SlaComplianceData {
  overall: {
    metCount: number;
    missedCount: number;
    total: number;
    complianceRate: number;
  };
  byJobType: SlaJobTypeStat[];
  tier?: SlaComplianceTier;
  bucket?: SlaComplianceBucket;
}

export function useSlaCompliance(
  jobTypeId?: string,
  from?: string,
  to?: string,
  bucket?: SlaComplianceBucket,
) {
  const [data, setData] = useState<SlaComplianceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (jobTypeId) params.set("jobTypeId", jobTypeId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<SlaComplianceData>(`/analytics/sla-compliance${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load SLA compliance data");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
